/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalService } from '../../../../../core/services';
import { WafApiService, WafOptionsModel } from '../../../../loadbalancer/shared';

@Component({
    selector: 'app-waf-options',
    templateUrl: './wafOptions.component.html'
})

export class WafOptionsComponent {

    /**
     * WAF options Model
     */
    public waf: WafOptionsModel = <WafOptionsModel>{};

    public wafOptionsForm: FormGroup;

    public staticForm: FormGroup;

    public openIDForm: FormGroup;

    public loginTypes = ['disabled', 'static', 'openID'];

    constructor(private wafApi: WafApiService,
                private modal: ModalService,
                private formBuilder: FormBuilder,
    ) {
        this.wafOptionsForm = this.getWafOptionsForm();

        this.staticForm = this.getStaticForm();

        this.openIDForm = this.getOpenIDForm();
    }

    /**
     * Build the waf options form
     * @returns {FormGroup}
     */
    private getWafOptionsForm() {
        return this.formBuilder.group({
            id: [],
            label: [],
            haproxyServiceID: [],
            haproxyWafBackend: [],
            ip: [],
            port: [],
            enabled: ['', Validators.required],
            wafACL: [],
            inboundAnomalyScore: ['5', Validators.required],
            outboundAnomalyScore: ['5', Validators.required],
            processRequestData: [],
            processResponseData: [],
            autoMode: [],
            proxyTimeout: ['60', Validators.required],
            cacheEnabled: [],
            overrideNoCache: [],
            cacheObjectSize: ['5120', Validators.required],
            doubleLoginMode: [],
            doubleLogin: []
        });
    }

    /**
     * Build the double login static form
     * @returns {FormGroup}
     */
    private getStaticForm() {
        return this.formBuilder.group({
            username: [],
            password: [],
            location: [],
            cookiePassword: []
        });
    }

    /**
     * Build the double login open ID form
     * @returns {FormGroup}
     */
    private getOpenIDForm() {
        return this.formBuilder.group({
            clientID: [],
            clientSecret: [],
            location: [],
            redirectURI: [],
            passPhrase: [],
            allowedDomains: []
        });
    }

    /**
     * Make the edit call to the api
     */
    public editWaf() {
        const wafOpts: WafOptionsModel = this.wafOptionsForm.value;
        switch (this.wafOptionsForm.value['doubleLoginMode']) {
            case 'static': {
                wafOpts.doubleLogin = this.staticForm.value;
                break;
            }
            case 'openID': {
                wafOpts.doubleLogin = this.openIDForm.value;
                break;
            }
        }

        this.wafApi.edit(this.waf.id, wafOpts).subscribe(
            () => {
                this.modal.close('waf-options');
            }
        );
    }

    /**
     * Get the waf config from the api
     * @returns {Observable<any>}
     */
    public getWaf(id: string) {
        this.wafApi.get(id).subscribe(
            waf => {
                this.waf = waf;
                this.setWafFormValues(waf);
            }
        );
    }

    /**
     * Set the values in the forms for the waf options
     * @param {WafOptionsModel} data
     */
    private setWafFormValues(data: WafOptionsModel) {
        // Use patchValue not value so it accepts missing values in the model
        this.wafOptionsForm.patchValue(data, { onlySelf: true });

        switch (this.wafOptionsForm.value['doubleLoginMode']) {
            case 'static': {
                this.staticForm.patchValue(data.doubleLogin, { onlySelf: true });
                break;
            }
            case 'openID': {
                this.openIDForm.patchValue(data.doubleLogin, { onlySelf: true });
                break;
            }
        }
    }

    /**
     * Close the waf options modal
     */
    public closeWafOptionsModal() {
        // Close the modal via the service
        this.modal.close('waf-options');
    }
}
